import { routes } from '@/config/routes'
import { useBestPerformingVaultsQuery } from '@/queries/bestPerformingVaults.query'
import type { BenchmarkGroup } from '@/types/analytics'
import type { ChartPeriod } from '@/types/periods'
import type { PerformingVault } from '@/utils/calculateBestPerformingVaults'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getValueColor } from '@/utils/styles/getValueColor'
import { formatAmount, formatDate } from '@vaultsfyi/common/utils'
import { Link, generatePath } from 'react-router'
import { Button } from '../common/button'
import { Loader } from '../common/loader'
import { ProtocolImage } from '../common/protocolImage'

interface Props {
  chartPeriod: ChartPeriod
  group: BenchmarkGroup
}

export const BestPerformingVaults = ({ chartPeriod, group }: Props) => {
  const {
    bestPerformingVaults: vaults,
    dataLastUpdated,
    isFetching,
  } = useBestPerformingVaultsQuery({
    period: chartPeriod,
    group,
  })

  if (isFetching || !vaults) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader />
      </div>
    )
  }

  const topVaults = vaults.slice(0, 5)

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2">
        {topVaults.map((item, i) => (
          <VaultRow key={`${item.vault.network}:${item.vault.vaultId}`} vault={item} rank={i + 1} />
        ))}
      </ul>
      {dataLastUpdated && (
        <p className="text-sm leading-none">
          Data last updated: {formatDate({ timestamp: dataLastUpdated, showTime: true })}
        </p>
      )}
    </div>
  )
}

function Sparkline({ vault }: { vault: PerformingVault }) {
  const data = vault.filteredHistoricalData
  if (data.length < 2) return null
  const values = data.map((p) => ((p.sharePrice - vault.firstPrice) / vault.firstPrice) * 100)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const W = 32
  const H = 16
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * W
      const y = H - ((v - min) / range) * H
      return `${x},${y}`
    })
    .join(' ')
  const color =
    vault.sharePriceDiff > 0
      ? 'rgb(var(--chart-green))'
      : vault.sharePriceDiff < 0
        ? 'rgb(var(--chart-red))'
        : 'rgb(var(--body-foreground))'
  return (
    <svg width={W} height={H} className="shrink-0">
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

const VaultRow = ({ vault, rank }: { vault: PerformingVault; rank: number }) => {
  return (
    <li
      className={cn(
        'flex items-center gap-2 rounded-xl border p-3',
        'relative transition-colors has-[:focus-visible]:border-focus has-[:hover]:border-accent has-[:focus-visible]:ring-2'
      )}
    >
      <span className="w-[18px] shrink-0 text-right text-body-foreground text-sm">{rank}</span>
      {vault.vault.protocol && <ProtocolImage protocol={vault.vault.protocol} className="size-5 shrink-0" />}
      <Button
        variant="default"
        size="default"
        className={cn(
          'min-w-0 flex-grow items-baseline justify-start gap-1 text-heading-foreground hocus:ring-0 max-xs:flex-wrap',
          'before:absolute before:inset-0 before:h-full before:w-full'
        )}
        asChild
      >
        <Link
          to={generatePath(routes.opportunity, { vaultId: vault.vault.vaultId, network: vault.vault.network })}
          target="_blank"
          rel="noopener noreferrer"
          prefetch="intent"
        >
          <p className="min-w-0 truncate text-sm">{vault.vault.name}</p>
          <span className="shrink-0 text-sm first-letter:uppercase">({getNetworkName(vault.vault.network)})</span>
        </Link>
      </Button>
      <span className={cn('shrink-0 font-semibold text-sm tabular-nums', getValueColor(vault.sharePriceDiff))}>
        {vault.sharePriceDiff > 0 && '+'}
        {formatAmount(vault.sharePriceDiff, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
      </span>
      <Sparkline vault={vault} />
    </li>
  )
}
